/**
 * PaymentsPanel — the billing/payments view for the pricing console (MANAGEMENT 14). The operator enters a
 * target lab id, loads its SubscriptionPayment history (newest first) via
 * GET /management/billing/labs/:id/payments, and can trigger a "create checkout" for that lab (the Chargily
 * test/simulated flow) from here. Append-only ledger: statuts flow en_attente → paye|echec (settled by the
 * webhook). Money is integer centimes rendered via the finance helper; ids/amounts/dates ride `.nv-ltr-island`.
 *
 * Self-contained 5-state DoD for the async region: idle (no lab selected) / loading / error / empty / the
 * live table. AA: labelled search field, a real submit Button, an accessible Table caption, role="alert"
 * error. FR i18n. Parent owns the checkout modal open-state; this only raises the callback.
 */
import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Table,
  type Column,
} from "@/ui";
import { useT } from "@/app";
import { formatDateTime } from "../lib";
import { formatCentimes, paymentStatutTone } from "../screens/pricing.lib";
import { useLabPayments } from "../screens/pricing.api";
import type { PaymentRow } from "../screens/pricing.types";

export interface PaymentsPanelProps {
  enabled: boolean;
  /** Raised with the entered lab id when the operator asks to create a checkout for it. */
  onCreateCheckout: (labId: string) => void;
}

export function PaymentsPanel({ enabled, onCreateCheckout }: PaymentsPanelProps) {
  const { t, locale } = useT();
  const [draft, setDraft] = useState("");
  const [labId, setLabId] = useState(""); // the committed id (submit-driven, not keystroke-driven)

  const query = useLabPayments(labId, enabled);
  const rows = query.data?.items ?? [];

  const commit = () => setLabId(draft.trim());

  const columns: ReadonlyArray<Column<PaymentRow>> = [
    {
      key: "created",
      header: t("mgmt.pricing.billing.col.created"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.createdAt ?? "",
      cell: (r) => <span className="nv-ltr-island">{formatDateTime(r.createdAt, locale)}</span>,
    },
    {
      key: "plan",
      header: t("mgmt.pricing.billing.col.plan"),
      sortable: true,
      sortAccessor: (r) => r.planCode,
      cell: (r) => <span className="nv-ltr-island">{r.planCode}</span>,
    },
    {
      key: "cycle",
      header: t("mgmt.pricing.billing.col.cycle"),
      cell: (r) => t(`mgmt.pricing.cycle.${r.cycle}`),
    },
    {
      key: "amount",
      header: t("mgmt.pricing.billing.col.amount"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.montantCentimes,
      cell: (r) => <span className="nv-ltr-island">{formatCentimes(r.montantCentimes)}</span>,
    },
    {
      key: "statut",
      header: t("mgmt.pricing.billing.col.statut"),
      sortable: true,
      sortAccessor: (r) => r.statut,
      cell: (r) => (
        <Badge tone={paymentStatutTone(r.statut)} glyph="●">
          {t(`mgmt.pricing.payStatut.${r.statut}`)}
        </Badge>
      ),
    },
    {
      key: "checkout",
      header: t("mgmt.pricing.billing.col.checkout"),
      mono: true,
      cell: (r) => (
        <span className="nv-ltr-island" style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>
          {r.chargilyCheckoutId ?? "—"}
        </span>
      ),
    },
  ];

  return (
    <Card
      title={t("mgmt.pricing.billing.title")}
      actions={
        labId ? (
          <Button size="sm" variant="secondary" onClick={() => onCreateCheckout(labId)}>
            {t("mgmt.pricing.card.checkout")}
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
          {t("mgmt.pricing.billing.subtitle")}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            commit();
          }}
          style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap", alignItems: "flex-end" }}
        >
          <div style={{ flex: "1 1 200px" }}>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              inputMode="numeric"
              placeholder={t("mgmt.pricing.billing.labId.placeholder")}
              aria-label={t("mgmt.pricing.billing.labId")}
              className="nv-ltr-island"
            />
          </div>
          <Button type="submit" disabled={!draft.trim()}>
            {t("mgmt.pricing.billing.load")}
          </Button>
        </form>

        {/* Async region — 5-state DoD scoped to the payments list */}
        {!labId ? (
          <EmptyState
            title={t("mgmt.pricing.billing.selectPrompt.title")}
            description={t("mgmt.pricing.billing.selectPrompt.desc")}
          />
        ) : query.isLoading ? (
          <LoadingState variant="table" rows={4} />
        ) : query.isError ? (
          <ErrorState
            message={query.error.message}
            code={query.error.code}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <Table
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            caption={t("mgmt.pricing.billing.title")}
            pageSize={12}
            emptyTitle={t("mgmt.pricing.billing.empty.title")}
            emptyDescription={t("mgmt.pricing.billing.empty.desc")}
          />
        )}
      </div>
    </Card>
  );
}
